/**
 * /cobranca/configuracao — Configuração do gateway de cobrança.
 *
 * Redesign FIN-10: casca .fin2 (breadcrumb + page-title + .cfg-grid +
 * .apikey-row + .block). Mesma lógica de carregar/salvar; multa/juros
 * regulados pelo CDC (máx 2% / 1% ao mês). Consome o backend existente.
 *
 * Permissão: cobranca:configurar.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiArrowLeft, FiSave } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { criarOuAtualizarConfiguracao, listarConfiguracoesCobranca } from "../../../services/cobranca";
import { PageContainer } from "../../../styles/shared";

const PROVIDERS = [
  { value: "mock", label: "Mock (dev/teste — gera dados fake)" },
  { value: "asaas", label: "Asaas (produção real)" },
];
const AMBIENTES = [
  { value: "HOMOLOGACAO", label: "Homologação (sandbox Asaas — teste)" },
  { value: "PRODUCAO", label: "Produção (cobrança real, com tarifa)" },
];

export default function ConfiguracaoCobranca() {
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unidadeId, setUnidadeId] = useState("");

  const podeConfigurar = isMaster || hasAllPermissions(["cobranca:configurar"]);
  const dis = !podeConfigurar;

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = await listarConfiguracoesCobranca(request);
      const list = Array.isArray(p) ? p : p?.results || [];
      if (list.length > 0) {
        setConfig(list[0]);
        setUnidadeId(list[0].business_unit);
      } else {
        // Multa e juros nascem ZERADOS de propósito, como no backend: o
        // percentual chega ao gateway e vira encargo no boleto do cliente
        // final. Pré-preencher 2% e 1% fazia a primeira configuração da
        // marcenaria já sair cobrando, sem ninguém ter decidido isso.
        // Quem quiser encargo digita — é uma decisão, não um default.
        setConfig({
          provider: "mock", ambiente: "HOMOLOGACAO",
          percentual_multa: "0.00", percentual_juros_mes: "0.00",
          dias_apos_vencimento_para_juros: 1, ativo: true,
          api_key_configurada: false, api_key_plain: "",
        });
        const u = await request("/api/unidades");
        const ulist = Array.isArray(u) ? u : u?.results || [];
        if (ulist.length > 0) setUnidadeId(ulist[0].id);
      }
    } catch (err) {
      setError(err?.message || "Erro ao carregar configuração.");
    } finally { setLoading(false); }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleSalvar = async () => {
    const body = { ...config, business_unit: unidadeId };
    delete body.id; delete body.created_at; delete body.updated_at;
    delete body.provider_display; delete body.ambiente_display;
    delete body.api_key_configurada;
    if (!body.api_key_plain) delete body.api_key_plain;
    await criarOuAtualizarConfiguracao(request, body, config?.id || null);
    await carregar();
  };

  const set = (campo, valor) => setConfig((c) => ({ ...c, [campo]: valor }));

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Cobranças</span><span className="crumb-sep">/</span>
            <span className="active">Configuração</span>
          </div>
          <h1 className="page-title">Configuração de Cobrança</h1>
          <div className="page-sub">Gateway (Boleto / PIX / Cartão) + multa e juros regulados pelo CDC</div>
        </div>
        <button type="button" className="btn btn--ghost" onClick={() => navigate("/cobranca/boletos")}>
          <FiArrowLeft size={14} aria-hidden="true" /> Voltar
        </button>
      </div>

      {error && <div className="empty"><div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {error}</div></div>}

      {!podeConfigurar && (
        <div className="empty" style={{ marginBottom: 14 }}>
          <div className="msg">Você não tem a permissão <code>cobranca:configurar</code> — campos em modo leitura.</div>
        </div>
      )}

      {loading && !config && <div className="empty"><div className="msg">Carregando…</div></div>}

      {config && (
        <div className="cfg-wrap" style={{ maxWidth: "none" }}>
          <div className="block">
            <div className="block__head"><div className="block__title">Gateway</div></div>
            <div className="block__body">
              <div className="cfg-grid">
                <div className="input-group">
                  <label className="input-label">Provedor *</label>
                  <select className="select" value={config.provider} disabled={dis} onChange={(e) => set("provider", e.target.value)}>
                    {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div className="input-group">
                  <label className="input-label">Ambiente *</label>
                  <select className="select" value={config.ambiente} disabled={dis} onChange={(e) => set("ambiente", e.target.value)}>
                    {AMBIENTES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                  <span className="input-help">
                    Ambiente: <span className={"badge no-dot " + (config.ambiente === "PRODUCAO" ? "success" : "warning")}>{config.ambiente === "PRODUCAO" ? "Produção" : "Homologação"}</span>
                  </span>
                </div>
              </div>

              {config.provider === "asaas" && (
                <div className="input-group" style={{ marginTop: 14 }}>
                  <label className="input-label">API Key Asaas</label>
                  <div className="apikey-row">
                    <span className={"badge no-dot " + (config.api_key_configurada ? "success" : "neutral")}>
                      {config.api_key_configurada ? "Configurada" : "Não configurada"}
                    </span>
                    <input className="input" type="password" style={{ flex: 1 }} disabled={dis}
                           placeholder={config.api_key_configurada ? "Preenchida (deixe em branco para manter)" : "Cole a API key de painel.asaas.com → Integrações → API"}
                           value={config.api_key_plain || ""} onChange={(e) => set("api_key_plain", e.target.value)} />
                  </div>
                  <span className="input-help">Guardada criptografada. Depois de salva, ela nunca mais aparece na tela.</span>
                </div>
              )}
            </div>
          </div>

          <div className="block">
            <div className="block__head"><div className="block__title">Multa e juros (regulado pelo CDC)</div></div>
            <div className="block__body">
              <div className="cfg-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                <div className="input-group">
                  <label className="input-label">Multa (%) — máx. 2</label>
                  <input className="input" type="number" step="0.01" min="0" max="2" disabled={dis}
                         value={config.percentual_multa} onChange={(e) => set("percentual_multa", e.target.value)} />
                </div>
                <div className="input-group">
                  <label className="input-label">Juros ao mês (%) — máx. 1</label>
                  <input className="input" type="number" step="0.01" min="0" max="1" disabled={dis}
                         value={config.percentual_juros_mes} onChange={(e) => set("percentual_juros_mes", e.target.value)} />
                </div>
                <div className="input-group">
                  <label className="input-label">Dias após vencimento</label>
                  <input className="input" type="number" min="0" disabled={dis}
                         value={config.dias_apos_vencimento_para_juros}
                         onChange={(e) => set("dias_apos_vencimento_para_juros", parseInt(e.target.value, 10) || 0)} />
                </div>
              </div>
            </div>
          </div>

          {podeConfigurar && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <AsyncButton variant="primary" onClick={handleSalvar} successMessage="Configuração salva">
                <FiSave size={14} aria-hidden="true" /> Salvar alterações
              </AsyncButton>
            </div>
          )}

          <div className="block">
            <div className="block__head"><div className="block__title">Como ativar o Asaas em produção</div></div>
            <div className="block__body" style={{ fontSize: 13, color: "var(--ink-2)" }}>
              <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                <li>Cadastrar a empresa em painel.asaas.com (gratuito)</li>
                <li>Pegar a API key em Integrações &gt; API</li>
                <li>Colar acima + trocar o ambiente para "Produção"</li>
                <li>Configurar o webhook no painel apontando para <span className="mono">/api/webhooks/asaas</span></li>
                <li>Salvar — as próximas cobranças já vão pelo Asaas e reconciliam automaticamente</li>
              </ol>
              <p style={{ marginTop: 10 }}>
                <strong>Tarifas Asaas (2026):</strong> R$ 1,99/boleto compensado · R$ 0,99/PIX · 2,99% + R$ 0,49/cartão — sem mensalidade.
              </p>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
